'use client';

// The shells' <main>, greyed and frozen in the phone app while offline on a
// page that needs a connection (BL-2422, mockup screen 11; ADR 066 — Offline
// Mode). The page stays mounted, so nothing typed on it is lost.

import type { ComponentPropsWithoutRef } from 'react';
import { usePathname } from 'next/navigation';
import { clsx } from 'clsx';
import { isOfflineBlocked } from '@auctogon/admin-isomorphic/isomorphic/offline/offline-routes';
import { useOfflineMode } from './OfflineModeProvider';

/** The shell's own `<main>` attributes, passed through unchanged. */
export type OfflineGateProps = ComponentPropsWithoutRef<'main'>;

/**
 * Render a shell's `<main>`. It replaces the element rather than wrapping it,
 * because the take-over shells place `.app-main` as a grid cell and the
 * dashboard's `<main>` is a flex child: a wrapper would break both layouts.
 *
 * Takes no domain props (ADR 029 — View Units): the mode comes from
 * `OfflineModeProvider`, the path from the router. While offline on a page
 * outside the offline-capable areas it adds `inert` (no taps, no focus, no
 * scrolling, out of the accessibility tree) and greys the page. Everything
 * outside `<main>` (the connectivity bar, the phone menu button, the rails)
 * stays live, so the operator can still leave.
 *
 * @param props - the `<main>` attributes the shell already sets.
 * @returns The page's `<main>`.
 */
export default function OfflineGate({ className, children, ...rest }: OfflineGateProps) {
  const { isOffline } = useOfflineMode();
  const pathname = usePathname() ?? '';
  const gated = isOfflineBlocked(pathname, isOffline);
  return (
    <main
      {...rest}
      inert={gated || undefined}
      data-offline-gated={gated ? 'true' : undefined}
      // The page's content fades, not <main> itself: <main> keeps its own
      // opaque surface (white, or the take-over's paper), so the shell frame
      // behind it (the sidebar's brand green) never tints the greyed page.
      className={clsx(className, gated && '[&>*]:opacity-50 [&>*]:grayscale')}
    >
      {children}
    </main>
  );
}
